import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {DashboardHomeExtras} from '../components/DashboardHomeExtras';
import {analyticsService, type BookingTrendData, type PlatformStats, type UserGrowthData} from '../services/analyticsService';
import {providerManagementService, type PlatformProvider} from '../services/providerManagement';
import {supportTicketManagementService, type SupportTicket} from '../services/supportTicketManagement';
import './DashboardPage.css';

const TREND_DAYS = 14;
const TICKET_BATCH = 40;
const PENDING_VERIFICATION_LIMIT = 20;

function sortPendingVerificationFifo(providers: PlatformProvider[]): PlatformProvider[] {
  return [...providers].sort((a, b) => {
    const ta = new Date(a.verification?.submittedAt ?? a.updatedAt).getTime();
    const tb = new Date(b.verification?.submittedAt ?? b.updatedAt).getTime();
    return ta - tb;
  });
}

function formatInr(n: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(n);
}

export const DashboardPage: React.FC = () => {
  const {admin} = useAuth();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [bookingTrends, setBookingTrends] = useState<BookingTrendData[]>([]);
  const [userGrowth, setUserGrowth] = useState<UserGrowthData[]>([]);
  const [chartsError, setChartsError] = useState<string | null>(null);
  const [chartsLoading, setChartsLoading] = useState(true);

  const [ticketsRaw, setTicketsRaw] = useState<SupportTicket[]>([]);
  const [ticketsError, setTicketsError] = useState<string | null>(null);
  const [ticketsLoading, setTicketsLoading] = useState(true);

  const [pendingVerification, setPendingVerification] = useState<PlatformProvider[]>([]);
  const [pendingVerificationError, setPendingVerificationError] = useState<string | null>(null);
  const [pendingVerificationLoading, setPendingVerificationLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStatsLoading(true);
        setStatsError(null);
        const s = await analyticsService.getPlatformStats();
        if (!cancelled) setStats(s);
      } catch (e: unknown) {
        if (!cancelled) {
          setStatsError(e instanceof Error ? e.message : 'Could not load platform metrics');
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setChartsLoading(true);
      setChartsError(null);
      setTicketsLoading(true);
      setTicketsError(null);
      setPendingVerificationLoading(true);
      setPendingVerificationError(null);
      const settled = await Promise.allSettled([
        analyticsService.getBookingTrends(TREND_DAYS, 'daily'),
        analyticsService.getUserGrowthTrends(TREND_DAYS),
        supportTicketManagementService.listSupportTickets({limit: TICKET_BATCH}),
        providerManagementService.listProviders({
          limit: PENDING_VERIFICATION_LIMIT,
          verificationStatus: 'pending_review',
        }),
      ]);
      if (cancelled) return;

      const trendsResult = settled[0];
      const growthResult = settled[1];
      const ticketsResult = settled[2];
      const pendingResult = settled[3];

      setBookingTrends(trendsResult.status === 'fulfilled' ? trendsResult.value : []);
      setUserGrowth(growthResult.status === 'fulfilled' ? growthResult.value : []);

      if (ticketsResult.status === 'fulfilled') {
        setTicketsRaw(ticketsResult.value.tickets);
      } else {
        setTicketsRaw([]);
        const r = ticketsResult.reason;
        setTicketsError(r instanceof Error ? r.message : 'Could not load support tickets');
      }

      if (pendingResult.status === 'fulfilled') {
        setPendingVerification(sortPendingVerificationFifo(pendingResult.value.providers));
      } else {
        setPendingVerification([]);
        const r = pendingResult.reason;
        setPendingVerificationError(
          r instanceof Error ? r.message : 'Could not load pending verification queue'
        );
      }

      const trendFailed = trendsResult.status === 'rejected';
      const growthFailed = growthResult.status === 'rejected';
      if (trendFailed && growthFailed) {
        const r = trendsResult.reason;
        setChartsError(r instanceof Error ? r.message : 'Could not load trend charts');
      } else if (trendFailed) {
        setChartsError('Booking trend unavailable');
      } else if (growthFailed) {
        setChartsError('User growth unavailable');
      } else {
        setChartsError(null);
      }

      setChartsLoading(false);
      setTicketsLoading(false);
      setPendingVerificationLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="dashboard-page">
      <div className="welcome-section">
        <h2>Welcome, {admin?.name || admin?.email}</h2>
        <p className="admin-role-badge">Role: {admin?.role?.replace('_', ' ')}</p>
        <p className="session-strip">
          <span>{admin?.email}</span>
          <span aria-hidden="true"> · </span>
          <span>{admin?.isActive ? 'Active' : 'Inactive'}</span>
          {admin?.lastLoginAt && (
            <>
              <span aria-hidden="true"> · </span>
              <span>Last sign-in {new Date(admin.lastLoginAt).toLocaleString()}</span>
            </>
          )}
        </p>
      </div>

      <section className="platform-overview" aria-labelledby="platform-overview-heading">
        <h3 id="platform-overview-heading">Platform at a glance</h3>
        <p className="section-lead">
          High-level volumes and activity. Open <Link to="/dashboard/analytics">Analytics</Link> for trends and
          breakdowns.
        </p>
        {statsLoading && <div className="overview-loading">Loading metrics…</div>}
        {statsError && <div className="overview-error">{statsError}</div>}
        {!statsLoading && stats && (
          <div className="dashboard-stats platform-stats-grid">
            <div className="stat-card">
              <div className="stat-label">Users</div>
              <div className="stat-value">{stats.totalUsers.toLocaleString('en-IN')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Providers</div>
              <div className="stat-value">{stats.totalProviders.toLocaleString('en-IN')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Bookings (all time)</div>
              <div className="stat-value">{stats.totalBookings.toLocaleString('en-IN')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Active bookings</div>
              <div className="stat-value">{stats.activeBookings.toLocaleString('en-IN')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Completed</div>
              <div className="stat-value">{stats.completedBookings.toLocaleString('en-IN')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Recorded revenue</div>
              <div className="stat-value">{formatInr(stats.totalRevenue)}</div>
            </div>
          </div>
        )}
      </section>

      <DashboardHomeExtras
        bookingTrends={bookingTrends}
        userGrowth={userGrowth}
        ticketsRaw={ticketsRaw}
        pendingVerificationProviders={pendingVerification}
        chartsLoading={chartsLoading}
        ticketsLoading={ticketsLoading}
        pendingVerificationLoading={pendingVerificationLoading}
        chartsError={chartsError}
        ticketsError={ticketsError}
        pendingVerificationError={pendingVerificationError}
      />

      <nav className="quick-links" aria-label="Common admin areas">
        <Link className="quick-link" to="/dashboard/users">
          Users
        </Link>
        <Link className="quick-link" to="/dashboard/providers">
          Providers
        </Link>
        <Link className="quick-link" to="/dashboard/bookings">
          Bookings
        </Link>
        <Link className="quick-link" to="/dashboard/support-tickets">
          Support
        </Link>
        <Link className="quick-link" to="/dashboard/system-settings">
          Settings
        </Link>
      </nav>
    </div>
  );
};
